import type { LoaderFunctionArgs } from '~/types/route-args';
import { fetchCategoryNeighbours } from '@modules/product/util/fetch-category-neighbours.server';
import type { CategoryNeighbours } from '@modules/product/util/fetch-category-neighbours.server';

export type {
    CategoryNeighbour,
    CategoryNeighbours,
} from '@modules/product/util/fetch-category-neighbours.server';

export const PRODUCT_NEIGHBOURS_API_ROUTE = '/remix-api/product/neighbours';

/** The neighbours, marked with the product they were looked up for. */
export interface ProductNeighboursAnswer extends CategoryNeighbours {
    /** Echoes the asking product, so a stale answer is never laid out under another product's foot. */
    for: number;
}

/**
 * The products either side of one in its category listing, for the foot of
 * its page: the next-product turn and the island's next-product offer.
 *
 * A resource route rather than a field of the product loader on purpose.
 * The lookup reads a hundred products out of the category's listing, and in
 * the loader it sat on every product page's first byte — paid by every
 * reader, for a foot most never reach. Fetched from the client instead, it
 * arrives long before the reader is anywhere near the foot, and the page's
 * own data — including the copy the turn warms — stays that much lighter.
 */
export const loader = async ({
    request,
    context,
}: LoaderFunctionArgs): Promise<ProductNeighboursAnswer> => {
    const url = new URL(request.url);
    const categoryLink = url.searchParams.get('category') ?? '';
    const productId = Number(url.searchParams.get('product'));
    if (!categoryLink || !Number.isInteger(productId) || productId <= 0) {
        throw new Response('Missing category or product', { status: 400 });
    }

    const neighbours = await fetchCategoryNeighbours({
        context,
        categoryLink,
        currentProductId: productId,
    });
    return { for: productId, ...neighbours };
};
